import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Textarea from '../../ui/inputs/Textarea';
import { Star, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';
import { useConfirm } from '../../ui/confirm/ConfirmContext';
import Select from '../../ui/selects/Select';

const DreamModal = ({ clientId, dreamId = null, scope, onClose, onSuccess, canWrite = true }) => {
  const isEdit = !!dreamId;
  const { addToast } = useToast();

  const [form, setForm] = useState({
    name: '',
    dreamValue: '',
    targetAge: '',
    notes: '',
    dreamType: 'withdrawal', // 'withdrawal' ou 'contribution'
  });

  const confirm = useConfirm();

  // Carrega dados do sonho (modo edição)
  useEffect(() => {
    if (isEdit) {
      fetch(`/app/planner/client/${clientId}/dashboard/dreams/dreams/${dreamId}?scope=${scope}`)
        .then((res) => res.json())
        .then((data) => setForm(camelizeKeys(data)))
        .catch((err) => {
          console.error('Erro ao carregar sonho', err);
          addToast({ type: 'error', message: 'Erro ao carregar dados do sonho' });
        });
    }
  }, [isEdit, clientId, dreamId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'EXCLUIR SONHO?',
      description: `Tem certeza que deseja excluir o sonho?`,
      confirmText: 'EXCLUIR',
    });

    if (!ok) return;

    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/dreams/dreams/${dreamId}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao excluir o sonho');
      }

      addToast({ type: 'success', message: 'Sonho excluído com sucesso!' });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = snakecaseKeys({ dream: form });

    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/dreams/dreams/${dreamId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/dreams/dreams?scope=${scope}`;

    const method = isEdit ? 'PATCH' : 'POST';
    const successMsg = isEdit ? 'Sonho atualizado com sucesso!' : 'Sonho cadastrado com sucesso!';

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar sonho');
      }

      onClose();
      addToast({ type: 'success', message: successMsg });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Star size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? 'Editar Sonho' : 'Cadastrar Sonho'}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          {/* <div className="col-span-2">
            <Input name="name" label="Identificação" value={form.name} onChange={handleChange} />
          </div> */}
          <Input name="name" label="Identificação" value={form.name} onChange={handleChange} />
          <Select
            name="dreamType"
            label="Tipo"
            value={form.dreamType}
            onChange={handleChange}
            options={[
              { value: 'withdrawal', label: 'Retirada' },
              { value: 'contribution', label: 'Aporte' },
            ]}
          />
          <CurrencyInput
            name="dreamValue"
            label="Valor do Sonho"
            value={form.dreamValue}
            onChange={handleChange}
            required
          />

          <Input type="number" name="targetAge" label="Idade" value={form.targetAge} onChange={handleChange} required />

          <Textarea
            name="notes"
            label="Observações"
            value={form.notes}
            onChange={handleChange}
            className="md:col-span-2"
            rows={4}
          />

          {canWrite && (
            <div className="flex justify-between items-center md:col-span-2">
              <span className="text-lg text-redSoft font-semibold underline cursor-pointer" onClick={handleDelete}>
                Excluir sonho
              </span>
              <Button type="submit">{isEdit ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR SONHO'}</Button>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
};

export default DreamModal;
